/**
 * RESPONSIBILITY: the @Video control — upload, preview, description.
 *
 * Registers with a tester + rank, exactly as every other widget does. There is no switch
 * on field type anywhere in the rendering path.
 *
 * THE PREVIEW PLAYS. A still frame would not tell an editor whether they uploaded the
 * right take, or whether the thing is silent, or whether it is twenty seconds of someone
 * setting up a tripod. It is muted and loops because that is how a video on a page
 * usually behaves, and a dialog that starts making noise is a dialog people close.
 */
import { useState } from 'react';
import { rankWith, optionIs, type ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { EMPTY_IMAGE, hasImage, publicPath, formatBytes, type ImageValue } from '@framework/core';
import { uploadMedia } from '../upload-media';

/** What the file picker offers. The server keeps the real list; this is the convenience. */
const ACCEPT = 'video/mp4,video/webm';

function VideoControlBase({ data, handleChange, path, label, uischema, errors }: ControlProps) {
  const opts = (uischema as { options?: Record<string, unknown> }).options ?? {};
  const folder = String(opts.folder ?? 'misc');
  const maxSizeMb = Number(opts.maxSizeMb ?? 24);
  const size = opts.size ? String(opts.size) : '';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [justUploaded, setJustUploaded] = useState<{ name: string; bytes: number } | null>(null);

  const video: ImageValue = { ...EMPTY_IMAGE, ...(data as Partial<ImageValue> | undefined) };

  async function upload(file: File) {
    setBusy(true); setErr(null);
    const r = await uploadMedia(file, folder, maxSizeMb, 'video');
    setBusy(false);

    if (!r.ok) { setErr(r.error); return; }
    setJustUploaded({ name: r.value.originalName, bytes: r.value.bytes });
    handleChange(path, { path: r.value.path, alt: video.alt });
  }

  return (
    <div className="ctl">
      <label className="ctl-label">
        {label}
        {size && <span className="ctl-size">{size}</span>}
        <span className="ctl-size ctl-size-soft">max {maxSizeMb} MB</span>
      </label>

      <div className="ctl-img">
        {/* The picture is the picker in the image control and the video is the picker
            here, for the same reason: the click target is the thing itself, and the file
            input covers it at zero opacity so the control stays keyboard-reachable. */}
        <label className="ctl-img-pick ctl-vid-pick"
               title={hasImage(video) ? 'Click to replace' : 'Click to choose'}>
          {hasImage(video)
            ? <video className="ctl-vid-preview" src={video.path} muted loop autoPlay playsInline />
            : <span className="ctl-img-empty">no video</span>}
          <span className="ctl-img-veil">
            {busy ? 'Uploading…' : hasImage(video) ? 'Replace' : 'Choose'}
          </span>
          <input type="file" accept={ACCEPT} disabled={busy}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
        </label>

        <input
          type="text" className="ctl-input" placeholder="What this video shows"
          value={video.alt}
          onChange={(e) => handleChange(path, { ...video, alt: e.target.value })}
        />

        {hasImage(video) && (
          <div className="ctl-img-bar">
            <div className="ctl-img-icons">
              {/* `download` is always present: without it the browser NAVIGATES to the file
                  and the video plays in a tab instead of saving. `true` lets the browser
                  name it from the address when there is no original filename to offer. */}
              <a className="ctl-img-icon ctl-img-get" href={`${video.path}?original`}
                 download={justUploaded?.name || true}
                 title="Download" aria-label="Download video">↓</a>
              <button type="button" className="ctl-img-icon ctl-img-drop"
                      onClick={() => handleChange(path, { ...EMPTY_IMAGE })}
                      title="Remove" aria-label="Remove video">✕</button>
            </div>

            <div className="ctl-img-meta">
              {justUploaded && <span className="ctl-img-name">{justUploaded.name}</span>}
              {justUploaded && <span>{formatBytes(justUploaded.bytes)}</span>}
              <span className="ctl-img-path" title={publicPath(video)}>{publicPath(video)}</span>
            </div>
          </div>
        )}

        {err && <span className="ctl-error ctl-img-err">{err}</span>}
      </div>
      {errors && <p className="ctl-error">{errors}</p>}
    </div>
  );
}

export const videoControl = {
  tester: rankWith(10, optionIs('widget', 'video')),
  renderer: withJsonFormsControlProps(VideoControlBase),
};
